/**
 * PropertyPhotoGallery.tsx
 *
 * Drag-and-drop gallery manager for property photos, without a shell of its
 * own. Two callers render it: PropertyPhotoGalleryModal, opened by clicking the
 * cover photo on the property page, and the Rentmatch publication form, which
 * shows it inline as a section (DEV-3180). Both talk to the same four
 * endpoints, so there is one data path and no copy of this logic anywhere.
 *
 * - Loads all images (id, url, position) from GET /properties/:id/gallery-images
 * - Reordering by native HTML5 drag and drop, saved on drop
 * - Upload new photos (resized client-side)
 * - Delete individual photos
 * - Cover photo = item at position 0 (shown with a badge)
 *
 * Every action persists as it happens, so the component owns no unsaved state
 * and needs no button of its own. That also settles an older trap: the save
 * button used to be a relabelled close button, so a broker who dragged a photo
 * and then closed lost the new order without being told.
 */

import * as documentApi from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.api";
import { IFile } from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.types";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  Button,
  EmptyState,
} from "@rentiohq/web-shared/dist/components";
import React, { useEffect, useRef, useState } from "react";
import { resizeImageFile } from "../../../../../utils/imageResize.utils";
import styles from "./PropertyPhotoGallery.module.css";

interface IPropertyImageItem {
  id: number;
  imageDocumentId: string | null;
  position: number;
  url: string | null;
  isOptimistic?: boolean;
}

interface IPropertyPhotoGalleryProps {
  propertyId: number;
  /** Called after anything is persisted, so the caller can refresh the cover. */
  onSaved: () => void;
}

export const PropertyPhotoGallery: React.FC<IPropertyPhotoGalleryProps> = ({
  propertyId,
  onSaved,
}) => {
  const [images, setImages] = useState<IPropertyImageItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Drag state
  const dragIndex = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Load images on mount
  useEffect(() => {
    loadImages();
  }, [propertyId]);

  const loadImages = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await api.get(`/properties/${propertyId}/gallery-images`, {
        isV2: true,
      });
      setImages((result as any).data?.data || []);
    } catch (err) {
      setError(getLocalizedText("property.gallery.error.load"));
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Native drag-and-drop ──────────────────────────────────────────────────

  const handleDragStart = (index: number) => {
    dragIndex.current = index;
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const fromIndex = dragIndex.current;
    setDragOverIndex(null);
    dragIndex.current = null;

    if (fromIndex === null || fromIndex === dropIndex) {
      return;
    }

    const previous = images;
    const reordered = Array.from(images);
    const [removed] = reordered.splice(fromIndex, 1);
    reordered.splice(dropIndex, 0, removed);

    // Shown immediately, then persisted. On failure the old order comes back,
    // because leaving the new one on screen would tell the broker their cover
    // photo changed when the server still has the previous one.
    setImages(reordered);

    try {
      setIsSavingOrder(true);
      setError(null);
      await api.post(`/properties/${propertyId}/gallery-reorder`, {
        data: { imageIds: reordered.map(img => img.id) },
        isV2: true,
      });
      onSaved();
    } catch (err) {
      setImages(previous);
      setError(getLocalizedText("property.gallery.error.save"));
    } finally {
      setIsSavingOrder(false);
    }
  };

  const handleDragEnd = () => {
    setDragOverIndex(null);
    dragIndex.current = null;
  };

  // ─── Upload ───────────────────────────────────────────────────────────────

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Reset file input so the same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = "";

    setIsUploading(true);
    setError(null);
    setUploadProgress({ current: 0, total: files.length });

    const localPreviews: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        setUploadProgress({ current: i + 1, total: files.length });
        const resized = await resizeImageFile(files[i]);

        // Show local blob URL immediately so there's no broken image while the
        // Lambda generates the thumbnail.
        const localUrl = URL.createObjectURL(resized as unknown as Blob);
        localPreviews.push(localUrl);
        setImages(prev => [
          ...prev,
          {
            id: Date.now() + i,
            imageDocumentId: null,
            position: prev.length + i,
            url: localUrl,
            isOptimistic: true,
          },
        ]);

        const preSignedUrl = await documentApi.uploadDocumentToS3(
          resized as unknown as IFile,
        );
        await api.post(`/properties/${propertyId}/gallery-upload`, {
          data: {
            preSignedUrl,
            filename: resized.name,
          },
          isV2: true,
        });
      }
      await loadImages();
      localPreviews.forEach(url => URL.revokeObjectURL(url));
      onSaved(); // notify parent (e.g. refresh cover image)
    } catch (err) {
      localPreviews.forEach(url => URL.revokeObjectURL(url));
      setError(getLocalizedText("property.gallery.error.upload"));
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  // ─── Delete ───────────────────────────────────────────────────────────────

  const handleDelete = async (imageId: number) => {
    confirm({
      title: getLocalizedText("property.gallery.delete_photo"),
      info: getLocalizedText("property.gallery.delete_confirm"),
      primaryActions: [
        {
          title: getLocalizedText("system.delete"),
          onPress: async () => {
            try {
              setError(null);
              await api.post(`/properties/${propertyId}/gallery-delete`, {
                data: { imageId },
                isV2: true,
              });
              // Optimistic update
              setImages(prev => prev.filter(img => img.id !== imageId));
              onSaved();
            } catch (err) {
              setError(getLocalizedText("property.gallery.error.delete"));
            }
          },
        },
      ],
    });
  };

  // ─── Upload button label ───────────────────────────────────────────────────

  const getUploadLabel = () => {
    if (!isUploading) return getLocalizedText("property.gallery.upload_photos");
    if (uploadProgress && uploadProgress.total > 1) {
      return `${getLocalizedText("system.loading")} (${
        uploadProgress.current
      }/${uploadProgress.total})`;
    }
    return `${getLocalizedText("system.loading")}…`;
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    // No padding of its own: both places this gallery is used are modals, and
    // a modal already insets its content. A second inset only pushed the
    // photos out of line with the fields above them.
    <div>
      {/* Error banner — Rentio Banner component */}
      {error && (
        <div className={styles.errorBannerWrapper}>
          <Banner variation="error" title={error} hasDismiss />
        </div>
      )}

      {/* Always mounted, whatever is or is not drawn around it: the button in
          the empty state opens this same picker, and a ref to something that
          was conditioned away is a button that does nothing at all. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Upload button row.

          Held back while the gallery is empty: the empty state below carries
          the same button and the same sentence, so a property without photos
          offered the identical thing twice, and only one of the two said what
          the box is for.

          It does come back the moment an upload starts, and the empty state
          steps aside for it, because the progress count lives on this button
          and a first upload would otherwise run with nothing moving on
          screen. */}
      {!isLoading && (images.length > 0 || isUploading) ? (
        <div className={styles.uploadArea}>
          <Button
            appearance="outline"
            isSubmitting={isUploading}
            isDisabled={isUploading}
            onClick={() => !isUploading && fileInputRef.current?.click()}
          >
            {getUploadLabel()}
          </Button>
          <p className={styles.hint}>
            {getLocalizedText("property.gallery.drag_hint")}
          </p>
        </div>
      ) : null}

      {/* Loading state — Rentio EmptyState with isLoading */}
      {isLoading ? <EmptyState isLoading size="small" /> : null}

      {/* Empty state — Rentio EmptyState component */}
      {!isLoading && images.length === 0 && !isUploading ? (
        <EmptyState
          icon="house"
          // Wider than the default, which broke the one sentence it holds onto
          // a second line with half the block still empty beside it.
          maxWidth={520}
          heading={getLocalizedText("property.gallery.empty")}
          action={{
            content: getLocalizedText("property.gallery.upload_photos"),
            onClick: () => fileInputRef.current?.click(),
          }}
        >
          {getLocalizedText("property.gallery.drag_hint")}
        </EmptyState>
      ) : null}

      {/* Gallery grid */}
      {!isLoading && images.length > 0 ? (
        <div className={styles.gridWrapper}>
          <div className={styles.grid}>
            {images.map((image, index) => (
              <div
                key={image.id}
                className={`${styles.card} ${
                  dragOverIndex === index ? styles.cardDragOver : ""
                }`}
                draggable={!isSavingOrder}
                onDragStart={() => handleDragStart(index)}
                onDragOver={e => handleDragOver(e, index)}
                onDrop={e => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                {/* Cover badge */}
                {index === 0 && (
                  <div className={styles.coverBadge}>
                    {getLocalizedText("property.gallery.cover_photo")}
                  </div>
                )}

                {/* Image */}
                <div className={styles.imageWrapper}>
                  {image.url ? (
                    <img
                      src={image.url}
                      alt={`${index + 1}`}
                      className={styles.image}
                    />
                  ) : (
                    <div className={styles.imagePlaceholder} />
                  )}
                </div>

                {/* Delete — Rentio Button appearance="danger" */}
                <Button
                  appearance="danger"
                  size="slim"
                  onClick={e => {
                    (e as any).stopPropagation?.();
                    handleDelete(image.id);
                  }}
                  style={{
                    position: "absolute",
                    top: "4px",
                    right: "4px",
                    minWidth: "unset",
                    padding: "2px 6px",
                  }}
                >
                  ×
                </Button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
